// Phase 2 (grade-2 ceiling) — `array` concept visual for multiplication.
//
// Renders an m×n array (rows × cols) of dots: the canonical area/array model
// for multiplication (e.g. 3 rows of 5 = 3×5). ADDITIVE — registers itself
// under the "array" kind; no skill targets it yet, so it stays invisible to
// kids until content opts in. Plugs in with NO edit to MathVisual or the
// session screens (the registry resolves it by key).
//
// READS its visualData via the shared MathRendererProps contract:
//   • rows ← `a`  (number of rows / groups)
//   • cols ← `b`  (number per row)
// MA-659 (MA-651 batch 3, RENDER-B): when `a`/`b` are absent this renders
// NOTHING (`return null`) rather than degrading to a single row of `total`
// dots — `total` is `Number(expectedAnswer)`, so that degrade drew the
// answer itself (the batch-2 `place_value_blocks` failure mode). `return
// null` is the repo's own convention for "no faithful picture is possible"
// (see `ElapsedTimeClockRenderer.tsx`, `FractionComparisonRenderer.tsx`);
// no faithful picture is possible without both dimensions, so no picture is
// drawn.
import type { MathRendererProps } from "./rendererRegistry";

const DOT = "var(--pal-orange, #d97742)";
const CELL = 34; // px pitch between dot centers (matches Dots() in the registry)
const R = 13; // dot radius (matches the built-in counter radius)
const PAD = 18; // first dot center offset

function ArrayRenderer({ a, b, total }: MathRendererProps) {
  void total; // MA-659: no longer used for a degrade fallback — see header.
  // MA-659 round 2 (N5): `== null` (not `=== undefined`) so an authored
  // `a: null` or a non-finite value (e.g. a stray string) also takes the
  // no-picture path instead of falling through to `safe()`'s `?? 1` clamp,
  // which would otherwise silently draw a faithful-looking 1-row/1-col
  // picture for a genuinely missing dimension.
  if (a == null || b == null || !Number.isFinite(Number(a)) || !Number.isFinite(Number(b))) {
    // MA-659 (RENDER-B) / round 2 (MF-6): no faithful picture without both
    // dimensions — return null (repo convention), never a degrade that
    // draws `total` = expectedAnswer.
    return null;
  }
  // rows × cols from a/b, clamped to a sane upper bound so an oversized
  // authored a/b can't emit thousands of <circle>s / a runaway viewBox.
  const MAX = 12;
  const safe = (v: number) => Math.max(1, Math.min(MAX, Math.floor(Number.isFinite(v) ? v : 1)));
  const rows = safe(a);
  const cols = safe(b);
  const width = PAD * 2 + (cols - 1) * CELL;
  const height = PAD * 2 + (rows - 1) * CELL;
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      style={{ maxWidth: Math.min(360, width) }}
      role="img"
      aria-label={`Array of ${rows} rows by ${cols} columns`}
    >
      {Array.from({ length: rows }).map((_, r) =>
        Array.from({ length: cols }).map((_, c) => (
          <circle key={r * cols + c} cx={PAD + c * CELL} cy={PAD + r * CELL} r={R} fill={DOT} />
        )),
      )}
    </svg>
  );
}

export default ArrayRenderer;
